import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  getLastActivity, isIdleExpired, markActivity, parseLogoutBroadcast,
} from "../lib/sessionActivity";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "input", "scroll"];
const CHECK_EVERY_MS = 15000;
const HEARTBEAT_EVERY_MS = 2 * 60 * 1000;
const TIMEOUT_MSG = "You were signed out after 60 minutes of inactivity.";

function isEmbedPlayer(src, host) {
  return typeof src === "string" && src.includes(host);
}

// Ask cross-origin players (Vimeo, YouTube with enablejsapi=1) to report
// playback progress to us via postMessage.
function subscribeEmbeds() {
  document.querySelectorAll("iframe").forEach((f) => {
    const src = f.getAttribute("src") || "";
    try {
      if (isEmbedPlayer(src, "player.vimeo.com")) {
        f.contentWindow?.postMessage(JSON.stringify({ method: "addEventListener", value: "timeupdate" }), "*");
        f.contentWindow?.postMessage(JSON.stringify({ method: "addEventListener", value: "playProgress" }), "*");
      } else if (isEmbedPlayer(src, "youtube.com/embed") || isEmbedPlayer(src, "youtube-nocookie.com/embed")) {
        f.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: "cpg-idle" }), "*");
      }
    } catch { /* detached frame */ }
  });
}

// Mounted once inside the router. Logs the user out after 60 minutes
// without real activity; a genuinely playing video counts as activity.
export function SessionTimeoutManager() {
  const { status, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const expiring = useRef(false);
  const lastBeatFor = useRef(0);
  const lastBeatAt = useRef(0);
  const ytTime = useRef({});

  const signOut = useCallback(async (reason, { callApi = true } = {}) => {
    if (expiring.current) return;
    expiring.current = true;
    // Navigate before auth state flips so <Protected>'s own redirect
    // doesn't replace this URL (and the toast) mid-transition.
    const next = window.location.pathname;
    navigate(`/?next=${encodeURIComponent(next)}${reason === "timeout" ? "&reason=timeout" : ""}`, { replace: true });
    if (reason === "timeout") toast.info(TIMEOUT_MSG, { id: "session-timeout", duration: 10000 });
    try { window.sessionStorage.clear(); } catch { /* ignore */ }
    await logout({ callApi, reason });
  }, [logout, navigate]);

  useEffect(() => {
    if (status === "authenticated") {
      expiring.current = false;
      if (!getLastActivity()) markActivity(true);
    }
  }, [status]);

  useEffect(() => {
    if (status === "authenticated") markActivity();
  }, [location.pathname, status]);

  useEffect(() => {
    if (status !== "authenticated") return undefined;

    const onActivity = () => markActivity();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { capture: true, passive: true }));

    // Native <video>/<audio>: timeupdate only fires while time advances,
    // so paused, ended and stalled media never count.
    const onMediaTime = (e) => {
      const el = e.target;
      if (el && !el.paused && !el.ended) markActivity();
    };
    document.addEventListener("timeupdate", onMediaTime, true);

    const onMessage = (e) => {
      const origin = e.origin || "";
      let data = e.data;
      if (typeof data === "string") { try { data = JSON.parse(data); } catch { return; } }
      if (!data || typeof data !== "object") return;
      if (origin.includes("vimeo.com") && (data.event === "timeupdate" || data.event === "playProgress")) {
        markActivity();
      } else if (origin.includes("youtube") && data.event === "infoDelivery" && data.info) {
        const t = data.info.currentTime;
        const key = String(data.id || "yt");
        const advanced = typeof t === "number" && t !== ytTime.current[key];
        if (typeof t === "number") ytTime.current[key] = t;
        if (data.info.playerState === 1 && advanced) markActivity();
      }
    };
    window.addEventListener("message", onMessage);

    const check = () => {
      if (isIdleExpired()) { signOut("timeout"); return; }
      const last = getLastActivity();
      const now = Date.now();
      if (last > lastBeatFor.current && now - lastBeatAt.current >= HEARTBEAT_EVERY_MS) {
        lastBeatFor.current = last;
        lastBeatAt.current = now;
        api.post("/auth/heartbeat").catch(() => {});
      }
      subscribeEmbeds();
    };
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    check();

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity, { capture: true }));
      document.removeEventListener("timeupdate", onMediaTime, true);
      window.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [status, signOut]);

  // Cross-tab: a logout/timeout in any tab signs every tab out.
  useEffect(() => {
    const onStorage = (e) => {
      const msg = parseLogoutBroadcast(e);
      if (msg && status === "authenticated") signOut(msg.reason, { callApi: false });
    };
    const onExpired = () => { if (status === "authenticated") signOut("timeout"); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("cpg:session-expired", onExpired);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cpg:session-expired", onExpired);
    };
  }, [status, signOut]);

  return null;
}
